import { ref, readonly } from 'vue'

const STORAGE_KEY = 'idp:last-scope'

export interface IdpLastScope {
  team: string
  subTeam?: string
  env?: string
  cluster?: string
  service?: string
}

const scope = ref<IdpLastScope | null>(readStored())

function readStored(): IdpLastScope | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<IdpLastScope>
    if (!parsed.team || typeof parsed.team !== 'string') return null
    return {
      team: parsed.team,
      subTeam: parsed.subTeam || undefined,
      env: parsed.env === 'staging' ? 'staging' : 'production',
      cluster: parsed.cluster || undefined,
      service: parsed.service || undefined
    }
  } catch {
    return null
  }
}

function persist(next: IdpLastScope | null) {
  scope.value = next
  if (typeof localStorage === 'undefined') return
  if (!next) {
    localStorage.removeItem(STORAGE_KEY)
    return
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
}

const first = (value: unknown): string => {
  if (Array.isArray(value)) return String(value[0] ?? '').trim()
  return String(value ?? '').trim()
}

/** Keep the last team / sub-team / service the user opened in IDP. */
export function rememberIdpScope(query: Record<string, unknown>) {
  const team = first(query.team)
  if (!team) return
  persist({
    team,
    subTeam: first(query.subTeam) || undefined,
    env: first(query.env) === 'staging' ? 'staging' : 'production',
    cluster: first(query.cluster) || undefined,
    service: first(query.service) || undefined
  })
}

const titleCase = (s: string) =>
  s
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ')

export function idpWorkspaceTarget(current: IdpLastScope | null): {
  path: string
  query: Record<string, string>
} {
  if (!current?.team) return { path: '/idp/services', query: {} }
  const query: Record<string, string> = {
    team: current.team,
    env: current.env === 'staging' ? 'staging' : 'production'
  }
  if (current.cluster) query.cluster = current.cluster
  if (current.subTeam) query.subTeam = current.subTeam
  if (current.subTeam && current.service) {
    query.service = current.service
    query.tab = 'overview'
    return { path: '/idp/workspace', query }
  }
  if (current.subTeam) return { path: '/idp/subteam', query }
  return { path: '/idp/team', query }
}

export function idpWorkspaceHint(current: IdpLastScope | null): string {
  if (!current?.team) return 'Pick a team in the catalog'
  const team = titleCase(current.team)
  if (!current.subTeam) return `${team} — pick a sub-team`
  const sub = titleCase(current.subTeam)
  if (current.service) return `${team} / ${sub} · ${current.service}`
  return `${team} / ${sub}`
}

export function useIdpLastScope() {
  return readonly(scope)
}
